/**
 * The message bubble shown above the launcher while the messenger is closed:
 * new replies and automatic (trigger) messages. Shared by the website loader
 * and the dashboard's trigger builder; no framework dependencies.
 */
export const PREVIEW_STYLES = `
.previews { display: flex; flex-direction: column; gap: 10px; max-width: 340px; }
.open .previews { display: none; }
.preview { position: relative; display: flex; align-items: flex-start; gap: 14px; padding: 20px 22px; background: #fff; color: #0f172a;
  border: 1px solid #e5e7eb; border-radius: 24px; box-shadow: 0 6px 28px rgba(0,0,0,.10); cursor: pointer;
  font-size: 15px; line-height: 1.5; text-align: left; animation: pop .3s cubic-bezier(.2,.8,.2,1); }
.preview:hover { box-shadow: 0 8px 32px rgba(0,0,0,.16); }
.preview:focus-visible { outline: 2px solid var(--brand); outline-offset: 2px; }
.preview .avatar { flex: none; width: 36px; height: 36px; border-radius: 50%; background: var(--brand); color: var(--brand-fg, #fff);
  display: grid; place-items: center; font-size: 14px; font-weight: 600; overflow: hidden; }
.preview .avatar img { width: 100%; height: 100%; object-fit: cover; }
.preview .content { min-width: 0; flex: 1; }
.preview .body { display: -webkit-box; -webkit-line-clamp: 6; -webkit-box-orient: vertical; overflow: hidden;
  white-space: pre-line; word-break: break-word; }
.preview .meta { margin-top: 8px; font-size: 13px; color: #64748b; }
.preview .dismiss { position: absolute; top: -8px; right: -8px; width: 24px; height: 24px; border-radius: 50%; border: 0;
  background: #fff; color: #64748b; cursor: pointer; box-shadow: 0 1px 6px rgba(0,0,0,.2); font-size: 15px; line-height: 24px;
  opacity: 0; transition: opacity .15s; }
.preview:hover .dismiss, .preview:focus-within .dismiss { opacity: 1; }
@media (hover: none) { .preview .dismiss { opacity: 1; } }
@keyframes pop { from { opacity: 0; transform: translateY(8px) scale(.97); } }
@media (prefers-reduced-motion: reduce) { .preview { animation: none; } }
`;

export interface PreviewBubble {
    name: string;
    avatarUrl?: string | null;
    body: string;
    /** ISO time for the "· 5m" label. */
    at: string;
}

export function escapeHtml(value: string) {
    return value.replace(
        /[&<>"']/g,
        (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
    );
}

/** "now", "5m", "3h", "2d". */
export function relativeTime(iso: string, now = Date.now()): string {
    const minutes = Math.floor((now - new Date(iso).getTime()) / 60_000);
    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    if (minutes < 60 * 24) return `${Math.floor(minutes / 60)}h`;
    return `${Math.floor(minutes / (60 * 24))}d`;
}

function metaText(bubble: Pick<PreviewBubble, 'name' | 'at'>) {
    return `${bubble.name} · ${relativeTime(bubble.at)}`;
}

/** Fills a `.preview` element (role=button) with the avatar, text, sender and time. */
export function populatePreview(card: HTMLElement, bubble: PreviewBubble) {
    card.className = 'preview';
    card.setAttribute('role', 'button');
    card.tabIndex = 0;
    card.dataset.at = bubble.at;
    card.dataset.name = bubble.name;
    card.innerHTML = `
        <span class="avatar">${
            bubble.avatarUrl
                ? `<img src="${escapeHtml(bubble.avatarUrl)}" alt="">`
                : escapeHtml(bubble.name.charAt(0).toUpperCase())
        }</span>
        <span class="content"><div class="body">${escapeHtml(bubble.body)}</div><div class="meta">${escapeHtml(metaText(bubble))}</div></span>
        <button class="dismiss" type="button" aria-label="Dismiss">×</button>`;
}

/** Re-renders the "· 5m" labels; call every minute. */
export function refreshPreviewTimes(root: ParentNode) {
    root.querySelectorAll<HTMLElement>('.preview[data-at]').forEach((card) => {
        const meta = card.querySelector('.meta');
        if (meta) meta.textContent = metaText({ name: card.dataset.name ?? '', at: card.dataset.at! });
    });
}
